import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import Common from 'helpers/intl-common';
import cx from 'classnames';

import useShareProjectPage from './hooks/useShareProjectPage';

// Menu
import { Button } from '@companycam/slab-web';
import OutsideClickObserver from 'components/shared/OutsideClickObserver';
import CCClipboard from 'components/shared/CCClipboard';
import steven from 'helpers/steven';

// #region icons and styled-components
const Container = styled.div`
  padding: var(--cc_size_spacing_l);
`;
const ShareTitle = styled.h3`
  margin-bottom: var(--cc_size_spacing_m);
`;
const ShareInfo = styled.div`
  text-align: center;
  font-style: italic;
  font-size: var(--cc_size_text_s);
`;
// #endregion

const ProjectPageShareActions = ({ pageId, shared, shareUrl }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const { shareProjectPage } = useShareProjectPage();

  useEffect(() => {
    let timer = null;
    if (copied) {
      timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  const dropdownClasses = cx('dropdown', {
    'show-contents': showShareMenu,
  });

  const handleShare = useCallback(() => {
    // Do nothing if the page is already shared
    if (!shared) {
      shareProjectPage(pageId);
    }
    if (!showShareMenu) {
      steven.elementClicked({
        id: 'project_page_share',
        content: Common.share.defaultMessage,
        type: 'button',
        action: 'project_page_share_clicked',
      });
    }
    setShowShareMenu(!showShareMenu);
  }, [pageId, shared, showShareMenu, shareProjectPage]);

  return (
    <OutsideClickObserver
      active={showShareMenu}
      onClickOutside={() => {
        setShowShareMenu(false);
      }}
    >
      <Button
        color="secondary"
        size="small"
        onClick={handleShare}
        design="minimal"
        data-testid="page-modal__Share"
      >
        <FormattedMessage id={Common.share.id} />
      </Button>
      <div className={dropdownClasses} data-testid="page-modal__action-Share">
        <div
          className="dropdown-menu dropdown-menu--align-right"
          style={{ width: 450 }}
        >
          <Container>
            <ShareTitle>
              <FormattedMessage
                id="ProjectPage.shareAction.sharePage"
                defaultMessage="Share Page"
                description="message to the user describing the adjacent link"
              />
            </ShareTitle>
            {shared ? (
              <>
                <div
                  className="link-copy-box-container"
                  data-controller="clipboard"
                  data-clipboard-success-text="Copied!"
                >
                  <input
                    type="text"
                    defaultValue={shareUrl}
                    className="link-copy-box"
                    readOnly
                    data-clipboard-target="source"
                  />
                  <CCClipboard
                    id="page-share-action-copy-link"
                    copy={shareUrl}
                    onCopy={() => setCopied(true)}
                  >
                    <>
                      <i className="mdi mdi-link-variant" />
                      {copied ? (
                        <FormattedMessage id={Common.share_copied.id} />
                      ) : (
                        <FormattedMessage id={Common.share_copyLink.id} />
                      )}
                    </>
                  </CCClipboard>
                </div>
                <ShareInfo>
                  <FormattedMessage
                    id="ProjectPage.shareAction.sharePageDescription"
                    defaultMessage="Anyone with this link can view the page"
                    description="message to the user describing who will have access to the link"
                  />
                </ShareInfo>
              </>
            ) : (
              <FormattedMessage id={Common.loading.id} />
            )}
          </Container>
        </div>
      </div>
    </OutsideClickObserver>
  );
};

ProjectPageShareActions.propTypes = {
  pageId: PropTypes.string.isRequired,
  shared: PropTypes.bool.isRequired,
  shareUrl: PropTypes.string,
};

export default ProjectPageShareActions;
